import { useCallback, useEffect, useRef, useState } from 'react'
import useLocalStorage from './useLocalStorage'
import {
  setAccessToken,
  setAge,
  setGender,
  setMemberId,
  setMemberType,
  setPartnerId,
} from 'core/singleton'
import API from 'core/api'
import { AppProps } from 'App'
import { AccessTokenData } from '@types'

/**
 * @Name useInitializeSession
 * @description 세션 정보 초기화 훅
 * @description 모듈에서는 사용 금지, TokenWebLib에서 이미 처리하고 있음
 */
const useInitializeSession = ({ props }: { props: AppProps }) => {
  const [isSessionInitialized, setIsSessionInitialized] = useState<boolean>(false)
  const isSessionInitializingRef = useRef<boolean>(false)

  const { accessToken, partnerId } = props

  const [storedGuestTokenData, setStoredGuestTokenData] = useLocalStorage<AccessTokenData>({
    key: `accessToken${partnerId ? `_${partnerId}` : ''}`,
    initialValue: {
      accessToken: undefined,
      partnerId,
    },
  })

  const setUserData = useCallback(
    ({
      age,
      gender,
      memberId,
      memberType,
    }: Pick<TokenResponse, 'age' | 'gender' | 'memberId' | 'memberType'>): void => {
      setAge(age)
      setGender(gender)
      setMemberId(memberId)
      setMemberType(String(memberType))
    },
    [],
  )

  const validateAndGetTokenData = useCallback(
    async (token: string): Promise<TokenResponse | undefined> => {
      try {
        return await API.getValidationToken(token)
      } catch {
        return
      }
    },
    [],
  )

  const getGuestTokenData = useCallback(async (): Promise<TokenResponse | undefined> => {
    try {
      return await API.getGuestToken()
    } catch {
      return
    }
  }, [])

  const initializeSessionToGuestData = useCallback((): void => {
    getGuestTokenData().then((tokenData) => {
      if (!tokenData) {
        setIsSessionInitialized(true)
        isSessionInitializingRef.current = false

        return
      }

      setAccessToken(tokenData.accessToken)
      setUserData({ ...tokenData })

      setStoredGuestTokenData({
        accessToken: tokenData.accessToken,
        partnerId: tokenData.partnerId,
        memberId: tokenData.memberId,
        date: new Date(),
      })

      setIsSessionInitialized(true)
      isSessionInitializingRef.current = false
    })
  }, [getGuestTokenData, setStoredGuestTokenData, setUserData])

  /**
   * 세션 초기화 useEffect
   */
  useEffect(() => {
    if (isSessionInitialized || isSessionInitializingRef.current) return

    isSessionInitializingRef.current = true

    if (partnerId) {
      setPartnerId(partnerId)
    }

    if (!accessToken || accessToken === 'undefined') {
      if (storedGuestTokenData.accessToken && storedGuestTokenData.partnerId === partnerId) {
        validateAndGetTokenData(storedGuestTokenData.accessToken).then((tokenData) => {
          if (tokenData) {
            setAccessToken(tokenData.accessToken)
            setUserData({ ...tokenData })

            setIsSessionInitialized(true)
            isSessionInitializingRef.current = false

            return
          } else {
            initializeSessionToGuestData()
          }
        })
      } else {
        initializeSessionToGuestData()
      }

      return
    }

    validateAndGetTokenData(accessToken).then((tokenData) => {
      if (!tokenData) {
        initializeSessionToGuestData()

        return
      }

      setAccessToken(tokenData.accessToken)
      setUserData({ ...tokenData })

      setIsSessionInitialized(true)
      isSessionInitializingRef.current = false
    })
  }, [
    accessToken,
    isSessionInitialized,
    partnerId,
    storedGuestTokenData,
    initializeSessionToGuestData,
    setStoredGuestTokenData,
    setUserData,
    validateAndGetTokenData,
  ])

  return { isSessionInitialized }
}

export default useInitializeSession
